import { Component, Inject, OnInit, AfterViewInit, OnDestroy, Input } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { HelpArticle } from '../../models';
import { HelpServiceBase } from '../../services';
import { TellerOnlineAppService } from 'teller-online-libraries/core';
import { TellerOnlineMessageService } from 'teller-online-libraries/shared';

@Component({
    selector: 'teller-online-help',
    templateUrl: './help.component.html',
    styleUrls: ['./help.component.scss'],
    host: {
        class: 'help-view'
    }
})
export class TellerOnlineHelpComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() useRailLayout: boolean = false;
    @Input() titleCaseOverrides: { [key: string]: string } = {};

    public helpArticles: { [key: string]: { group: string | null, articles: HelpArticle[] }[] } = {};
    public showScrollToTop: boolean = false;

    private _scrollContainer: Element | null = null;
    private _onScroll = () => {
        this.showScrollToTop = (this._scrollContainer?.scrollTop ?? 0) > 300;
    };

    constructor(
        public helpService: HelpServiceBase,
        public appService: TellerOnlineAppService,
        private messageService: TellerOnlineMessageService,
        private route: ActivatedRoute,
        private router: Router,
        @Inject(DOCUMENT) private document: Document,
    ) { }

    ngOnInit(): void {
        // Group articles by tag, then by group within each tag
        const availableTags = this.helpService.getAvailableTags();
        availableTags.forEach(tag => {
            const articles = this.helpService.articles.filter(a => a.tag === tag);
            if (articles.length > 0) {
                this.helpArticles[tag] = this._groupArticles(articles);
            }
        });

        this.appService.finishPageLoading();
    }

    private _groupArticles(articles: HelpArticle[]): { group: string | null, articles: HelpArticle[] }[] {
        const ungrouped = articles.filter(a => !a.group);
        const grouped = articles.filter(a => !!a.group);

        const groupNames: string[] = [];
        grouped.forEach(a => {
            if (!groupNames.includes(a.group!)) {
                groupNames.push(a.group!);
            }
        });

        const result: { group: string | null, articles: HelpArticle[] }[] = [];

        if (ungrouped.length > 0) {
            result.push({ group: null, articles: ungrouped });
        }

        groupNames.forEach(name => {
            result.push({ group: name, articles: grouped.filter(a => a.group === name) });
        });

        return result;
    }

    ngAfterViewInit(): void {
        // Listen for scroll on the mat-sidenav-content container (or fall back to window)
        this._scrollContainer = this.document.querySelector('mat-sidenav-content') ?? this.document.documentElement;
        this._scrollContainer.addEventListener('scroll', this._onScroll);

        // Auto scroll to the help section if the tag is in the url
        this.route.fragment.subscribe((anchor) => {
            if (anchor) {
                setTimeout(() => {
                    this._scrollToAnchor(anchor);
                }, 200);
            }
        });
    }

    ngOnDestroy(): void {
        this._scrollContainer?.removeEventListener('scroll', this._onScroll);
    }

    public slugify(value: string): string {
        return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    }

    public groupAnchor(tag: string, group: string): string {
        return tag + '-' + this.slugify(group);
    }

    public onClick_scrollToAnchor(anchor: string): void {
        this._updateFragment(anchor);
        this._scrollToAnchor(anchor);
    }

    public onClick_scrollToTop(): void {
        this._updateFragment(null);
        this._scrollContainer?.scrollTo({ top: 0, behavior: 'smooth' });
    }

    public onClick_copyBookmarkLink(bookmark: string): void {
        this._updateFragment(bookmark);
        this._scrollToAnchor(bookmark);
        const url = this.document.defaultView?.location.href.split('#')[0] + '#' + bookmark;
        this.document.defaultView?.navigator.clipboard.writeText(url);
        this.messageService.notification('Link copied to clipboard', 'success', 2000);
    }

    private _updateFragment(fragment: string | null): void {
        const url = this.router.url.split('#')[0] + (fragment ? '#' + fragment : '');
        history.replaceState(null, '', url);
    }

    private _scrollToAnchor(anchor: string): void {
        this.document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth' });
    }
}
